import { RiCloseLine, RiCheckLine } from "react-icons/ri";

const InvoiceConfirmation = ({ invoice, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Review Invoice</h2>
            <button
              onClick={onCancel}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <RiCloseLine className="w-6 h-6" />
            </button>
          </div>

          {/* Invoice Details */}
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Customer Details</h3>
                <div className="space-y-1 text-gray-600">
                  <p>{invoice.customerName}</p>
                  <p>{invoice.email}</p>
                  <p>{invoice.phone}</p>
                  <p className="whitespace-pre-line">{invoice.address}</p>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Invoice Details</h3>
                <div className="space-y-1 text-gray-600">
                  <p>Invoice Number: {invoice.invoiceNumber}</p>
                  <p>
                    Date: {new Date(invoice.invoiceDate).toLocaleDateString()}
                  </p>
                  <p>
                    Due Date: {new Date(invoice.dueDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Items</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                        Description
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">
                        Quantity
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">
                        Price
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">
                        Tax
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invoice.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-gray-900">
                          {item.description}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-right">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-right">
                          ${item.price.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-right">
                          {item.tax}%
                        </td>
                        <td className="px-4 py-3 text-gray-900 text-right">
                          $
                          {(
                            item.quantity *
                            item.price *
                            (1 + item.tax / 100)
                          ).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td
                        colSpan="4"
                        className="px-4 py-3 text-right font-medium"
                      >
                        Subtotal
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        $
                        {invoice.items
                          .reduce(
                            (sum, item) => sum + item.quantity * item.price,
                            0,
                          )
                          .toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td
                        colSpan="4"
                        className="px-4 py-3 text-right font-medium"
                      >
                        Tax Total
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        $
                        {invoice.items
                          .reduce(
                            (sum, item) =>
                              sum +
                              (item.quantity * item.price * item.tax) / 100,
                            0,
                          )
                          .toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td
                        colSpan="4"
                        className="px-4 py-3 text-right font-medium text-lg"
                      >
                        Total
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-lg">
                        $
                        {invoice.items
                          .reduce(
                            (sum, item) =>
                              sum +
                              item.quantity * item.price * (1 + item.tax / 100),
                            0,
                          )
                          .toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Additional Information */}
            {(invoice.notes || invoice.terms) && (
              <div className="space-y-4">
                {invoice.notes && (
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Notes</h3>
                    <p className="text-gray-600 whitespace-pre-line">
                      {invoice.notes}
                    </p>
                  </div>
                )}
                {invoice.terms && (
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      Terms & Conditions
                    </h3>
                    <p className="text-gray-600 whitespace-pre-line">
                      {invoice.terms}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-4 pt-6 border-t">
            <button
              onClick={onCancel}
              className="btn btn-secondary min-w-[120px]"
            >
              Back to Edit
            </button>
            <button
              onClick={onConfirm}
              className="btn btn-primary min-w-[120px] inline-flex items-center justify-center gap-2"
            >
              <RiCheckLine className="w-5 h-5" />
              Proceed to Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceConfirmation;
